<template>
  <header class="header" :class="{ sticky: isSticky }" id="mainHeader">
    <div class="logo" @click="router.push('/')">
      <div>
        <img
          src="@/assets/images/logo/jiecang-1.png"
          alt="logo"
        />
      </div>
    </div>
    <nav :class="{ active: isMenuOpen }">
      <ul>
        <!-- 首页 -->
        <li>
          <router-link
            to="/"
            :class="{ active: route.path === '/' }"
            @click="closeMenu"
          >
            首页
          </router-link>
        </li>
        <li :class="{ 'has-dropdown': true, open: isProductOpen }">
          <router-link to="" @click.prevent="handleProductClick">
            产品
          </router-link>
          <ol :class="{ show: isProductOpen }">
            <li>
              <router-link
                to="/product"
                :class="{ active: route.path === '/product' }"
                @click="closeMenu"
              >
                产品中心
              </router-link>
            </li>
            <li>
              <router-link
                to="/product/database-audit"
                :class="{ active: route.path === '/product/database-audit' }"
                @click="closeMenu"
              >
                数据库审计平台
              </router-link>
            </li>
            <li>
              <router-link
                to="/product/ops-security"
                :class="{ active: route.path === '/product/ops-security' }"
                @click="closeMenu"
              >
                运维安全管控平台
              </router-link>
            </li>
            <li>
              <router-link
                to="/product/ai-ops-platform"
                :class="{ active: route.path === '/product/ai-ops-platform' }"
                @click="closeMenu"
              >
                智能运维管理平台
              </router-link>
            </li>
            <li>
              <router-link
                to="/product/biosample-mgmt"
                :class="{ active: route.path === '/product/biosample-mgmt' }"
                @click="closeMenu"
              >
                患者信息及生物样本采集管理系统
              </router-link>
            </li>
            <li>
              <router-link
                to="/product/auto-ops-resource"
                :class="{ active: route.path === '/product/auto-ops-resource' }"
                @click="closeMenu"
              >
                自动化运维数字资源管理系统
              </router-link>
            </li>
          </ol>
        </li>
        <li>
          <router-link
            to="/service"
            :class="{ active: route.path === '/service' }"
            @click="closeMenu"
          >
            服务
          </router-link>
        </li>
        <li>
          <router-link
            to="/case"
            :class="{ active: route.path === '/case' }"
            @click="closeMenu"
          >
            客户案例
          </router-link>
        </li>
        <li>
          <router-link
            to="/about"
            :class="{ active: route.path === '/about' }"
            @click="closeMenu"
          >
            关于我们
          </router-link>
        </li>
        <li>
          <router-link
            to="/contact"
            :class="{ active: route.path === '/contact' }"
            @click="closeMenu"
          >
            联系我们
          </router-link>
        </li>
      </ul>
    </nav>
    <div
      class="burger"
      :class="{ active: isMenuOpen }"
      @click.stop="toggleMenu"
    >
      <div class="burger-line1"></div>
      <div class="burger-line2"></div>
      <div class="burger-line3"></div>
    </div>
  </header>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

// ==================== Router 实例 ====================
const route = useRoute();
const router = useRouter();

// ==================== 响应式状态 ====================
const isMenuOpen = ref(false);
const isSticky = ref(false);
const isProductOpen = ref(false);

// ==================== 方法 ====================

// 切换汉堡菜单
const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
  document.body.classList.toggle("menu-open", isMenuOpen.value);
  if (!isMenuOpen.value) {
    isProductOpen.value = false;
  }
};

// 关闭菜单
const closeMenu = () => {
  isMenuOpen.value = false;
  isProductOpen.value = false;
  document.body.classList.remove("menu-open");
};

// 处理产品菜单点击（移动端手风琴效果）
const handleProductClick = () => {
  if (window.innerWidth <= 1024) {
    // 移动端：切换下拉菜单
    isProductOpen.value = !isProductOpen.value;
  } else {
    // 桌面端：直接跳转
    router.push("/product");
    closeMenu();
  }
};

// 点击页面其他区域关闭菜单
const handleClickOutside = (e) => {
  if (window.innerWidth <= 1024) {
    const nav = document.querySelector(".header nav");
    const burger = document.querySelector(".burger");

    if (
      nav &&
      burger &&
      !nav.contains(e.target) &&
      !burger.contains(e.target)
    ) {
      closeMenu();
    }
  }
};

// 窗口大小改变时重置状态
const handleResize = () => {
  if (window.innerWidth > 1024) {
    closeMenu();
  }
};

// 吸顶效果
const handleScroll = () => {
  isSticky.value = window.scrollY > 10;
};

// 监听路由变化，自动关闭移动端菜单
watch(
  () => route.path,
  () => {
    if (window.innerWidth <= 1024) {
      closeMenu();
    }
  }
);

// ==================== 生命周期钩子 ====================
onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  window.addEventListener("resize", handleResize);
  window.addEventListener("scroll", handleScroll);
  handleScroll();
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("resize", handleResize);
  window.removeEventListener("scroll", handleScroll);
  document.body.classList.remove("menu-open");
});
</script>

<style scoped>
/* ==================== 导航栏主体 ==================== */
.header {
  width: 100%;
  height: 80px;
  display: grid;
  padding: 0 40px;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 200;
  background-color: transparent;
  transition: all var(--transition-speed) ease;
  border-bottom: none;
}

/* Logo区域 */
.header .logo {
  display: flex;
  gap: 12px;
  align-items: center;
  font-size: 24px;
  font-weight: 600;
  color: var(--primary-color);
  z-index: 210;
  cursor: pointer;
}

.header .logo img {
  width: 120px;
  height: 80px;
  object-fit: contain;
}

/* ==================== 导航菜单 ==================== */
.header nav {
  justify-self: end;
  height: 100%;
  display: flex;
  align-items: center;
}

.header nav > ul {
  height: 100%;
  list-style: none;
  display: flex;
  align-items: center;
  gap: 8px;
}

.header nav > ul > li {
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
}

/* 导航链接样式 */
.header nav > ul > li > a,
.header nav > ul > li > router-link {
  color: var(--text-color-lightest);
  text-decoration: none;
  font-size: 16px;
  padding: 8px 32px 8px 16px;
  transition: all var(--transition-speed) ease;
  display: block;
  white-space: nowrap;
  position: relative;
  cursor: pointer;
}

.header nav > ul > li > a:hover,
.header nav > ul > li > router-link:hover {
  color: var(--primary-color);
}

/* 当前激活状态 */
.header nav > ul > li > a.active,
.header nav > ul > li > router-link.active {
  color: var(--primary-color);
}

/* 下拉箭头 */
.header nav > ul > li.has-dropdown::after {
  content: "";
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 4px solid var(--text-color-lightest);
  transition: all var(--transition-speed) ease;
  pointer-events: none;
}

.header nav > ul > li.has-dropdown:hover::after,
.header nav > ul > li.has-dropdown.open::after {
  border-top-color: var(--primary-color);
  transform: translateY(-50%) rotate(180deg);
}

/* ==================== 下拉菜单 ==================== */
.header nav > ul > li > ol {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(10px);
  background-color: var(--dropdown-bg);
  min-width: 280px;
  padding: 12px 0;
  border-radius: 8px;
  box-shadow: var(--dropdown-shadow);
  opacity: 0;
  visibility: hidden;
  transition: all var(--transition-speed) ease;
  list-style: none;
}

.header nav > ul > li > ol::before {
  content: "";
  position: absolute;
  left: 0;
  top: -20px;
  width: 100%;
  height: 20px;
}

.header nav > ul > li:hover > ol {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(0);
}

.header nav > ul > li > ol li {
  height: auto;
  width: 100%;
}

.header nav > ul > li > ol li a,
.header nav > ul > li > ol li router-link {
  color: var(--text-color-dark);
  padding: 12px 24px;
  font-size: 14px;
  display: block;
  transition: all var(--transition-speed) ease;
  border-left: 3px solid transparent;
  text-decoration: none;
}

.header nav > ul > li > ol li a:hover,
.header nav > ul > li > ol li router-link:hover,
.header nav > ul > li > ol li a.active,
.header nav > ul > li > ol li router-link.active {
  background-color: rgba(16, 142, 233, 0.05);
  color: var(--primary-color);
  border-left-color: var(--primary-color);
  padding-left: 28px;
}

/* ==================== 汉堡菜单按钮 ==================== */
.header .burger {
  display: none;
  cursor: pointer;
  z-index: 210;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  background: transparent;
  border: none;
  margin-left: auto;
}

.header .burger div {
  width: 25px;
  height: 2px;
  background-color: var(--text-color-lightest);
  transition: all var(--transition-speed) ease;
  display: block;
}

.header .burger.active .burger-line1 {
  transform: rotate(45deg) translate(5px, 6px);
}

.header .burger.active .burger-line2 {
  opacity: 0;
}

.header .burger.active .burger-line3 {
  transform: rotate(-45deg) translate(5px, -6px);
}

/* ==================== 吸顶导航样式 ==================== */
.header.sticky {
  background-color: white;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
}

.header.sticky .logo {
  color: var(--primary-color);
}

.header.sticky nav > ul > li > a,
.header.sticky nav > ul > li > router-link {
  color: var(--text-color-darker);
}

.header.sticky nav > ul > li > a:hover,
.header.sticky nav > ul > li > router-link:hover,
.header.sticky nav > ul > li > a.active,
.header.sticky nav > ul > li > router-link.active {
  color: var(--primary-color);
}

.header.sticky nav > ul > li.has-dropdown::after {
  border-top-color: var(--text-color-darker);
}

.header.sticky nav > ul > li.has-dropdown:hover::after {
  border-top-color: var(--primary-color);
}

.header.sticky .burger div {
  background-color: var(--text-color-darker);
}

/* ==================== 移动端适配 ==================== */
@media (max-width: 1024px) {
  body.menu-open {
    overflow: hidden;
  }

  .header {
    padding: 0 20px;
    grid-template-columns: auto 1fr auto;
    height: 80px;
  }

  .header nav {
    position: fixed;
    left: -100%;
    top: 0;
    flex-direction: column;
    background-color: var(--mobile-menu-bg);
    width: 100%;
    height: 100vh;
    padding-top: 80px;
    text-align: left;
    transition: left var(--transition-speed) ease;
    overflow-y: auto;
    justify-self: auto;
    z-index: 199;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    box-sizing: border-box;
  }

  .header nav.active {
    left: 0;
  }

  .header nav > ul {
    flex-direction: column;
    width: 100%;
    height: auto;
    gap: 0;
  }

  .header nav > ul > li {
    margin: 0;
    border-bottom: 1px solid var(--border-color);
    width: 100%;
    height: auto;
    flex-direction: column;
    align-items: stretch;
  }

  .header nav > ul > li > a,
  .header nav > ul > li > router-link {
    font-size: 16px;
    width: 100%;
    padding: 20px 24px;
    color: var(--text-color-darker);
  }

  .header nav > ul > li > a:hover,
  .header nav > ul > li > router-link:hover {
    background-color: rgba(16, 142, 233, 0.05);
    color: var(--primary-color);
  }

  .header nav > ul > li.has-dropdown::after {
    top: 28px;
    right: 24px;
    border-top-color: var(--text-color-gray);
  }

  .header nav > ul > li.has-dropdown.open::after {
    transform: translateY(-50%) rotate(180deg);
    border-top-color: var(--primary-color);
  }

  /* 移动端下拉菜单 - 手风琴效果 */
  .header nav > ul > li > ol {
    position: static;
    transform: none;
    display: block !important;
    opacity: 1;
    visibility: inherit;
    width: 100%;
    background-color: var(--mobile-dropdown-bg);
    max-height: 0;
    overflow: hidden;
    transition: max-height var(--transition-speed) ease-out;
    padding: 0;
    margin: 0;
    border-radius: 0;
    box-shadow: none;
    min-width: auto;
  }

  .header nav > ul > li > ol::before {
    display: none;
  }

  .header nav > ul > li > ol.show {
    max-height: 500px;
    transition: max-height var(--transition-speed) ease-in;
  }

  .header nav > ul > li > ol li a,
  .header nav > ul > li > ol li router-link {
    padding: 16px 24px 16px 48px;
    border-left: none;
    border-bottom: 1px solid var(--border-color);
    color: var(--text-color-dark-gray);
  }

  .header nav > ul > li > ol li a:hover,
  .header nav > ul > li > ol li router-link:hover,
  .header nav > ul > li > ol li a.active,
  .header nav > ul > li > ol li router-link.active {
    background-color: rgba(16, 142, 233, 0.08);
    color: var(--primary-color);
    padding-left: 52px;
    border-left: none;
  }

  .header nav > ul > li > ol li:last-child a,
  .header nav > ul > li > ol li:last-child router-link {
    border-bottom: none;
  }

  .header nav > ul > li:hover > ol {
    opacity: 1;
    visibility: visible;
    transform: translateX(0) translateY(0);
  }

  .header .burger {
    display: flex;
  }

  .header.sticky .burger div {
    background-color: var(--text-color-darker);
  }
}

@media (max-width: 480px) {
  .header {
    padding: 0 16px;
    height: 70px;
  }

  .header nav {
    top: 0;
    height: 100vh;
    padding-top: 70px;
  }

  .header nav > ul > li > a,
  .header nav > ul > li > router-link {
    padding: 18px 20px;
  }

  .header nav > ul > li > ol li a,
  .header nav > ul > li > ol li router-link {
    padding: 14px 20px 14px 40px;
  }
}
</style>